import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  viewChildren,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type ElementFormState,
  type ElementLabelPropsWithPositionAndWidth,
  type ElementLabelStyleProps,
  getBackground,
  getBorderColor,
  type InputElementMetaUiFeatures,
} from '@blaze-shared/elements-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { useEmitTracker } from '@blaze-shared/shared-util';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementLabelComponent } from '../../components/element-label/element-label.component';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { type ScoreValueStyle } from './score.models';
import {
  dataValueToScoreOption,
  getAdjacentScoreValue,
  getScoreTabIndex,
  getScoreValues,
  parseOptionalNumber,
  resolveCurrentScore,
  resolveScoreBounds,
} from './score.utils';

@UntilDestroy()
@Component({
  selector: 'be-score',
  imports: [ElementLabelComponent],
  templateUrl: './score.component.html',
  styleUrls: ['./score.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--be-score-default-color]': 'defaultCss().color',
    '[style.--be-score-default-fill]': 'defaultCss().fill',
    '[style.--be-score-default-outline]': 'defaultCss().outline',
    '[style.--be-score-default-outline-width]': 'defaultCss().outlineWidth',
    '[style.--be-score-default-radius]': 'defaultCss().radius',
    '[style.--be-score-default-font-family]': 'defaultCss().fontFamily',
    '[style.--be-score-default-font-size]': 'defaultCss().fontSize',
    '[style.--be-score-default-font-weight]': 'defaultCss().fontWeight',
    '[style.--be-score-hover-color]': 'hoverCss().color',
    '[style.--be-score-hover-fill]': 'hoverCss().fill',
    '[style.--be-score-hover-outline]': 'hoverCss().outline',
    '[style.--be-score-hover-outline-width]': 'hoverCss().outlineWidth',
    '[style.--be-score-hover-radius]': 'hoverCss().radius',
    '[style.--be-score-hover-font-family]': 'hoverCss().fontFamily',
    '[style.--be-score-hover-font-size]': 'hoverCss().fontSize',
    '[style.--be-score-hover-font-weight]': 'hoverCss().fontWeight',
    '[style.--be-score-selected-color]': 'selectedCss().color',
    '[style.--be-score-selected-fill]': 'selectedCss().fill',
    '[style.--be-score-selected-outline]': 'selectedCss().outline',
    '[style.--be-score-selected-outline-width]': 'selectedCss().outlineWidth',
    '[style.--be-score-selected-radius]': 'selectedCss().radius',
    '[style.--be-score-selected-font-family]': 'selectedCss().fontFamily',
    '[style.--be-score-selected-font-size]': 'selectedCss().fontSize',
    '[style.--be-score-selected-font-weight]': 'selectedCss().fontWeight',
  },
})
export class ScoreComponent
  extends CanvasElementBaseComponent<'score'>
  implements CanvasElementComponent<'score'>, OnInit, OnChanges
{
  private readonly optionButtons = viewChildren('scoreOption', { read: ElementRef });
  @Input() value: number | undefined;
  @Input() formState?: ElementFormState;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly schemaUpdateTracker = useEmitTracker();

  private readonly min = signal(1);
  private readonly max = signal(5);
  private readonly defaultValue = signal<number | undefined>(undefined);
  private readonly dataValue = signal<number | undefined>(undefined);
  readonly elementKeyRoot = 'b-score';
  elementKey!: string;
  labelEnabled = true;
  labelText = '';
  isReadonly = false;

  readonly defaultStyle = signal<ScoreValueStyle | undefined>(undefined);
  readonly hoverStyle = signal<ScoreValueStyle | undefined>(undefined);
  readonly selectedStyle = signal<ScoreValueStyle | undefined>(undefined);
  readonly defaultCss = computed(() => this.toCss(this.defaultStyle()));
  readonly hoverCss = computed(() => this.toCss(this.hoverStyle()));
  readonly selectedCss = computed(() => this.toCss(this.selectedStyle()));

  styles: Record<string, string> = {};
  validationStyles: Record<string, string> = {};

  readonly values = computed(() => getScoreValues(this.min(), this.max()));

  readonly currentScore = computed(() =>
    resolveCurrentScore(this.dataValue(), this.defaultValue(), this.values()),
  );

  get hasValidationError(): boolean | undefined {
    return this.formState?.errors?.required;
  }

  get title(): ElementLabelPropsWithPositionAndWidth | undefined {
    return this.props?.content?.title;
  }

  get labelBlock(): ElementLabelStyleProps | undefined {
    return this.props?.style?.labelBlock;
  }

  get titleColor(): string | undefined {
    return this.props?.style?.general?.title?.color1;
  }

  get titleIconColor(): string | undefined {
    return this.title?.iconColor?.color1;
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }

      if (this.schemaUpdateTracker.shouldEmit(this.getEventSchema('change'))) {
        this.setDefaultEventSchema('change');
      }

      this.readContentProps();
      this.updateStyles();
    }

    if (changes?.['value']) {
      this.dataValue.set(parseOptionalNumber(this.value));
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);
        this.isReadonly = features.isReadOnly || !!this.props?.content?.options?.readonly;
        safeDetectChanges(this.cdr);
      });
  }

  selectValue(value: number): void {
    if (this.isReadonly) {
      return;
    }
    this.dataValue.set(value);
    this.updateOutput(value);
  }

  tabIndexFor(value: number): number {
    return getScoreTabIndex(value, this.currentScore(), this.values());
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.isReadonly) {
      return;
    }

    const next = this.nextValueForKey(event.key);
    if (next == null) {
      return;
    }

    event.preventDefault();
    this.selectValue(next);
    this.focusOption(next);
  }

  private nextValueForKey(key: string): number | undefined {
    const values = this.values();
    if (key === 'Home') {
      return values[0];
    }
    if (key === 'End') {
      return values.at(-1);
    }
    if (key === 'ArrowRight' || key === 'ArrowDown') {
      return getAdjacentScoreValue(values, this.currentScore(), 1);
    }
    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      return getAdjacentScoreValue(values, this.currentScore(), -1);
    }
    return undefined;
  }

  private focusOption(value: number): void {
    const index = this.values().indexOf(value);
    this.optionButtons().at(index)?.nativeElement.focus();
  }

  private readContentProps(): void {
    this.labelEnabled = this.props?.content?.title?.enabled ?? true;
    const options = this.props?.content?.options;
    const bounds = resolveScoreBounds(options?.min, options?.max);
    this.min.set(bounds.min);
    this.max.set(bounds.max);
    this.defaultValue.set(parseOptionalNumber(options?.defaultValue));
    this.isReadonly = !!options?.readonly || this.isReadonly;
  }

  private updateOutput(data: number): void {
    const schema = this.getEventSchema('change');
    if (!schema) {
      return;
    }

    const output = dataValueToScoreOption(data);
    const isInitialData = !this.outputEmitTracker.hasPrevious();
    if (!this.outputEmitTracker.shouldEmit(output)) {
      return;
    }

    this.output.emit({
      eventType: 'change',
      data: output,
      isInitialData,
      schema,
    });
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const { general, labelBlock } = style || {};
    if (!style || !title || !general || !labelBlock) {
      return;
    }

    this.defaultStyle.set(style.value?.default);
    this.hoverStyle.set(style.value?.hover);
    this.selectedStyle.set(style.value?.selected);

    const styles: Record<string, string> = {};
    if (title.position === 'top') {
      styles['flex-direction'] = 'column';
    } else if (title.position === 'right') {
      styles['flex-direction'] = 'row-reverse';
    }
    this.styles = styles;
  }

  private toCss(style: ScoreValueStyle | undefined): Record<string, string | undefined> {
    if (!style) {
      return {};
    }

    const fill = style.fill ? getBackground(style.fill) : undefined;
    return {
      color: style.color?.color1,
      fill: fill === 'none' ? 'transparent' : fill,
      outline: style.outline ? getBorderColor(style.outline) : undefined,
      outlineWidth: style.outlineWidth,
      radius: style.radius,
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      fontWeight: style.fontFamily
        ? this.fontsService.getFontWeight(
            style.fontFamily,
            (style.fontWeight || '$t.fontWeight') as FontWeightOption,
          )
        : undefined,
    };
  }
}
